import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authClient, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Upload } from "lucide-react";
import { sfx } from "@/lib/sounds";

export default function CreatorApply() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    why_creator: "",
    content_type: "hybrid",
    schedule: "weekly",
    content_plan: "",
    youtube_url: "",
  });

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    authClient.get("/creators/application/me")
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [user]);

  const update = (key, value) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();

    if (
      form.why_creator.trim().length < 10 ||
      form.content_plan.trim().length < 10 ||
      !form.content_type ||
      !form.schedule
    ) {
      sfx.error();
      toast.error("Please complete all four fields with enough detail.");
      return;
    }

    setBusy(true);
    try {
      const { data } = await authClient.post("/creators/apply", {
        why_creator: form.why_creator.trim(),
        content_type: form.content_type,
        schedule: form.schedule,
        content_plan: form.content_plan.trim(),
        youtube_url: form.youtube_url.trim(),
      });

      setStatus(data.application);
      sfx.levelup();
      toast.success("Creator application submitted!", {
        description: "The LostDC Media team can now review your application.",
      });
    } catch (err) {
      sfx.error();
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) return null;

  if (status && status.status !== "rejected") {
    const approved = status.status === "approved";
    const color = approved ? "#22c55e" : "#FFBF00";

    return (
      <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="creator-status">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#FFBF00]/15 grid place-items-center text-[#FFBF00] mb-4">
            <Sparkles size={26} />
          </div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#FFBF00]">
            LostDC Media
          </div>
          <h1 className="font-hero text-3xl sm:text-4xl mt-2">
            {approved ? "You're a Creator!" : "Application Received"}
          </h1>
          <p className="text-white/60 mt-3 max-w-md mx-auto">
            {approved
              ? "Welcome to the LostDC Creator Program!"
              : "Your application is waiting for review. You will be notified when a decision is made."}
          </p>
          <div
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full glass"
            style={{ borderColor: `${color}55` }}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: color }} />
            <span className="text-xs uppercase tracking-widest font-mono" style={{ color }}>
              {status.status.replace("_", " ")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-creator-apply">
      <div className="max-w-3xl mx-auto">
        <SectionHeader
          eyebrow="LostDC Media"
          title="Apply to Be a Creator"
          subtitle="Tell us what you want to create, how often you plan to publish, and why you want to join."
        />

        <form onSubmit={submit} className="rounded-2xl glass p-5 sm:p-7 space-y-6">
          <div>
            <label className="text-sm font-semibold block mb-2">
              1. Why do you want to join LostDC Media?
            </label>
            <p className="text-xs text-white/50 mb-2">
              Explain your goals and what you hope to achieve with LostDC.
            </p>
            <Textarea
              required
              minLength={10}
              maxLength={2000}
              rows={4}
              value={form.why_creator}
              onChange={(e) => update("why_creator", e.target.value)}
              placeholder="I want to join LostDC Media because..."
              className="bg-black/60 border-white/10 text-white"
              data-testid="ca-why"
            />
          </div>

          <div>
            <label className="text-sm font-semibold block mb-2">
              2. What type of content will you create?
            </label>
            <select
              required
              value={form.content_type}
              onChange={(e) => update("content_type", e.target.value)}
              className="w-full rounded-md border border-white/10 bg-black/60 text-white p-3"
              data-testid="ca-content-type"
            >
              <option value="live">Live streams only</option>
              <option value="video">Videos only</option>
              <option value="shorts">Shorts only</option>
              <option value="hybrid">Hybrid (multiple content types)</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-semibold block mb-2">
              3. What is your expected upload or streaming schedule?
            </label>
            <select
              required
              value={form.schedule}
              onChange={(e) => update("schedule", e.target.value)}
              className="w-full rounded-md border border-white/10 bg-black/60 text-white p-3"
              data-testid="ca-schedule"
            >
              <option value="daily">Daily</option>
              <option value="multiple_per_week">Multiple times per week</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every two weeks</option>
              <option value="monthly">Monthly</option>
              <option value="flexible">Flexible / schedule varies</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-semibold block mb-2">
              4. Briefly describe your content plan.
            </label>
            <p className="text-xs text-white/50 mb-2">
              Tell us the games, topics, series, or ideas you plan to publish.
            </p>
            <Textarea
              required
              minLength={10}
              maxLength={2000}
              rows={4}
              value={form.content_plan}
              onChange={(e) => update("content_plan", e.target.value)}
              placeholder="I plan to create Minecraft gameplay, tutorials, live events..."
              className="bg-black/60 border-white/10 text-white"
              data-testid="ca-content-plan"
            />
          </div>

          <div>
            <label className="text-sm font-semibold block mb-2">
              5. Your YouTube channel (optional)
            </label>
            <p className="text-xs text-white/50 mb-2">
              Add your channel URL or @handle so LostDC can automatically sync eligible Minecraft videos and streams.
            </p>
            <input
              type="text"
              value={form.youtube_url}
              onChange={(e) => update("youtube_url", e.target.value)}
              maxLength={300}
              placeholder="https://www.youtube.com/@YourChannel or @YourChannel"
              className="w-full rounded-md border border-white/10 bg-black/60 text-white p-3"
              data-testid="ca-youtube-url"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="ca-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#FFBF00] text-black font-semibold uppercase tracking-wider hover:bg-[#FF8C00] transition-colors disabled:opacity-60"
          >
            <Upload size={16} />
            {busy ? "Submitting..." : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
}
